import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  adminApproveStoreAccessRequest,
  adminCloseStoreLifecycle,
  adminDeleteSupportMessage,
  adminDeleteUser,
  adminGetStoreLifecycleHistory,
  adminReactivateStoreLifecycle,
  adminReassignStoreOwner,
  adminRejectStoreAccessRequest,
  adminReplySupportMessage,
  notifyAdmin,
  notifyStoreRequestDecision,
  fetchPublicSiteContent,
  submitSupportMessage,
  supabase,
  uploadSupportAttachment,
} from '../supabaseClient';
import { buildPublicSiteContentMap } from '../publicSiteContent';
import { parseSupportMessageContent, sanitizeSupportMessageText, serializeSupportMessageContent } from '../supportMessageContent';
import './PlatformAdminSite.css';
import Dashboard from './Dashboard';
import LegacyRestaurantPage from './LegacyRestaurantPage';
import LandingPageEditor from './LandingPageEditor';
import StoreSiteManager from './StoreSiteManager';
import StaffPage from './StaffPage';
import StyleSettingsPage from './StyleSettingsPage';
import SupportMessageModal from './SupportMessageModal';
import { getStoreHost, getStoreOnlineUrl } from '../storeDomains';

const STORE_SECTION_CONFIG = {
  admin: { fileName: 'admin.html', title: 'Admin' },
  display: { fileName: 'display.html', title: 'Display' },
  products: { fileName: 'products.html', title: 'Products' },
  modifiers: { fileName: 'modifiers.html', title: 'Modifiers' },
  store: { fileName: 'store.html', title: 'Store Settings' },
  website: { fileName: null, title: 'Website' },
};

const CHROME_PREFERENCE_KEY = 'ss_workspace_chrome_hidden';

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '';
const getLifecycleStatus = (restaurant) => (restaurant?.lifecycle_status || 'active');
const getAttachmentList = (attachments = []) => attachments.filter((attachment) => attachment?.url);
const triggerBrowserDownload = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'attachment';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
const downloadAttachment = async (attachment) => {
  if (!attachment?.url) return;
  try {
    const response = await fetch(attachment.url);
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    triggerBrowserDownload(blobUrl, attachment.fileName || 'attachment');
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    triggerBrowserDownload(attachment.url, attachment.fileName || 'attachment');
  }
};
const downloadAttachments = (attachments = []) => {
  getAttachmentList(attachments).forEach((attachment, index) => {
    window.setTimeout(() => {
      downloadAttachment({
        ...attachment,
        fileName: attachment.fileName || `attachment-${index + 1}`,
      });
    }, index * 120);
  });
};
const collectMessageAttachments = (parsedMessage, parsedReply, thread) => {
  const seen = new Set();
  return [
    ...getAttachmentList(parsedMessage?.attachments),
    ...getAttachmentList(parsedReply?.attachments),
    ...thread.flatMap((entry) => getAttachmentList(entry.attachments || [])),
  ].filter((attachment) => {
    const key = `${attachment.url}|${attachment.fileName || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const PlatformAdminSite = ({ page = 'overview', user, role = 'admin', isAdmin = false, session = null, storeSection = 'products' }) => {
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const [restaurants, setRestaurants] = useState([]);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messagesReady, setMessagesReady] = useState(true);
  const [requestActionId, setRequestActionId] = useState('');
  const [messageActionId, setMessageActionId] = useState('');
  const [messageReplyDrafts, setMessageReplyDrafts] = useState({});
  const [messageReplyFiles, setMessageReplyFiles] = useState({});
  const [messageReplyPreviews, setMessageReplyPreviews] = useState({});
  const [notice, setNotice] = useState('');
  const [branding, setBranding] = useState(() => {
    try {
      const cached = localStorage.getItem('ss_public_site_content_v2');
      if (cached) return buildPublicSiteContentMap(JSON.parse(cached))?.branding || {};
    } catch {}
    return {};
  });

  useEffect(() => {
    fetchPublicSiteContent()
      .then(rows => {
        try { localStorage.setItem('ss_public_site_content_v2', JSON.stringify(rows)); } catch {}
        setBranding(buildPublicSiteContentMap(rows)?.branding || {});
      })
      .catch(() => {});
  }, []);
  const [storeActionId, setStoreActionId] = useState('');
  const [storeHistoryById, setStoreHistoryById] = useState({});
  const [expandedStoreHistoryId, setExpandedStoreHistoryId] = useState(null);
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [accountDeleteDetails, setAccountDeleteDetails] = useState('');
  const [accountDeleteFiles, setAccountDeleteFiles] = useState([]);
  const [accountDeletePreviews, setAccountDeletePreviews] = useState([]);
  const [accountDecisionDrafts, setAccountDecisionDrafts] = useState({});
  const [completedDeletionSectionOpen, setCompletedDeletionSectionOpen] = useState(false);
  const [expandedCompletedRequestId, setExpandedCompletedRequestId] = useState(null);
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [selectedInbox, setSelectedInbox] = useState('all'); // email | 'requests' | 'archived' | 'all'
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [expandedRequestId, setExpandedRequestId] = useState(null);
  const [expandedNavKey, setExpandedNavKey] = useState(() => {
    const path = window.location.pathname;
    if (path.startsWith('/dashboard/site-content')) return 'site-content';
    if (storeSection === 'admin' || storeSection === 'store') return 'store';
    if (storeSection === 'products' || storeSection === 'modifiers' || storeSection === 'style') return 'products';
    if (storeSection === 'website' || path.includes('/website')) return 'website';
    if (path.includes('/account')) return 'account';
    return null;
  });
  // Same check as chromeHidden below — a direct, unambiguous class on <main> instead of a
  // :has() selector that ties in specificity with .platform-admin-content.chrome-hidden and
  // depends on stylesheet order to win.
  const isFullscreenPreview = new URLSearchParams(window.location.search).get('fullscreen') === '1';
  const [chromeHidden, setChromeHidden] = useState(() => {
    // A link opened with ?fullscreen=1 (see PublicSiteManager's "Open full preview ↗")
    // always starts with the admin nav hidden, regardless of the saved preference — the
    // whole point of that link is a real, unconstrained browser window for previewing/
    // editing a page, not a narrower one with the nav just toggled off for this one visit.
    if (new URLSearchParams(window.location.search).get('fullscreen') === '1') return true;
    const savedPreference = localStorage.getItem(CHROME_PREFERENCE_KEY);
    if (savedPreference === '1') return true;
    if (savedPreference === '0') return false;
    return page === 'store-editor';
  });
  const [commandOpen, setCommandOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ss_theme') === 'dark');
  const toggleTheme = () => setDarkMode(d => { const next = !d; localStorage.setItem('ss_theme', next ? 'dark' : 'light'); return next; });
  const [commandQuery, setCommandQuery] = useState('');
  const deferredCommandQuery = useDeferredValue(commandQuery);

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      try {
        setLoading(true);
        const restaurantsQuery = isAdmin
          ? supabase.from('restaurants').select('*').order('created_at', { ascending: false })
          : supabase.from('restaurants').select('*').eq('store_owner_id', user.id).eq('lifecycle_status', 'active').order('created_at', { ascending: false });

        const { data, error: restaurantsError } = await restaurantsQuery;

        const { data: requestsData, error: requestsError } = await supabase
          .from('store_access_requests')
          .select('*')
          .order('requested_at', { ascending: false });

        const { data: messagesData, error: messagesError } = await supabase
          .from('support_messages')
          .select('*')
          .order('created_at', { ascending: false });

        if (restaurantsError) throw restaurantsError;
        if (requestsError) throw requestsError;
        if (messagesError && !messagesError.message?.includes('support_messages')) throw messagesError;
        if (active) {
          setRestaurants(data || []);
          setRequests(requestsData || []);
          setMessages(messagesError ? [] : (messagesData || []));
          setMessagesReady(!messagesError);
          setError('');
        }
      } catch (err) {
        if (active) setError(err.message || 'Failed to load Servsolo admin data.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadData();

    // Real-time subscription for support_messages
    const channel = supabase
      .channel('admin-support-messages')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_messages' }, () => {
        if (active) {
          supabase.from('support_messages').select('*').order('created_at', { ascending: false })
            .then(({ data }) => { if (active && data) setMessages(data); });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      active = false;
    };
  }, [isAdmin, user?.id]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandOpen((current) => !current);
        return;
      }

      if (event.key === 'Escape') {
        setCommandOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!commandOpen && commandQuery) {
      setCommandQuery('');
    }
  }, [commandOpen, commandQuery]);

  useEffect(() => {
    localStorage.setItem(CHROME_PREFERENCE_KEY, chromeHidden ? '1' : '0');
  }, [chromeHidden]);

  useEffect(() => {
    if (page === 'store-editor') {
      setChromeHidden(true);
    }
  }, [page]);

  const pendingInvitations = useMemo(
    () => restaurants.filter((restaurant) => restaurant.pending_store_owner_status === 'awaiting_confirmation'),
    [restaurants]
  );

  const pendingRequests = useMemo(
    () => requests.filter((request) => request.status === 'pending'),
    [requests]
  );

  const assignedOwners = useMemo(
    () => restaurants.filter((restaurant) => restaurant.store_owner_email),
    [restaurants]
  );

  const openMessages = useMemo(
    () => messages.filter((message) => ['open', 'in_progress'].includes(message.status)),
    [messages]
  );

  const accountRequests = useMemo(
    () => messages
      .filter((message) => message.source === 'account-deletion')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [messages]
  );

  const adminAccountDeletionRequests = useMemo(
    () => messages
      .filter((message) => message.source === 'account-deletion' && ['open', 'in_progress'].includes(message.status || 'open'))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [messages]
  );

  const completedAccountDeletionRequests = useMemo(
    () => messages
      .filter((message) => message.source === 'account-deletion' && ['resolved', 'archived'].includes(message.status || 'open'))
      .sort((a, b) => {
        const aTime = new Date(a.resolved_at || a.archived_at || a.created_at || 0).getTime();
        const bTime = new Date(b.resolved_at || b.archived_at || b.created_at || 0).getTime();
        return bTime - aTime;
      }),
    [messages]
  );

  const totalPendingRequests = pendingRequests.length + adminAccountDeletionRequests.length;

  const displayedMessages = useMemo(
    () => (isAdmin ? messages : messages.filter((message) => message.status !== 'archived')),
    [isAdmin, messages]
  );

  // ── Inbox grouping (admin only) ─────────────────────────────────────────
  // "known users" = messages where created_by_user_id is set
  // "requests" = messages where created_by_user_id is null (anonymous/non-user)
  // "archived" = status === 'archived'
  const inboxGroups = useMemo(() => {
    if (!isAdmin) return [];
    const active = messages.filter(m => m.status !== 'archived');
    const byEmail = new Map();
    const requests = [];
    for (const m of active) {
      if (m.created_by_user_id) {
        const key = m.from_email || m.created_by_user_id;
        if (!byEmail.has(key)) byEmail.set(key, { email: key, name: m.from_name || key, messages: [] });
        byEmail.get(key).messages.push(m);
      } else {
        requests.push(m);
      }
    }
    return { users: Array.from(byEmail.values()), requests };
  }, [isAdmin, messages]);

  const inboxMessages = useMemo(() => {
    if (!isAdmin) return displayedMessages;
    if (selectedInbox === 'archived') return messages.filter(m => m.status === 'archived');
    if (selectedInbox === 'requests') return (inboxGroups.requests || []);
    if (selectedInbox === 'all') return messages.filter(m => m.status !== 'archived');
    // specific user email
    const group = (inboxGroups.users || []).find(g => g.email === selectedInbox);
    return group ? group.messages : [];
  }, [isAdmin, selectedInbox, messages, displayedMessages, inboxGroups]);

  // (no auto-expand: user controls expand/collapse manually)

  useEffect(() => {
    if (!expandedRequestId && pendingRequests.length > 0) {
      setExpandedRequestId(pendingRequests[0].id);
    }
  }, [expandedRequestId, pendingRequests]);

  useEffect(() => {
    return () => {
      accountDeletePreviews.forEach((preview) => {
        if (preview) window.URL.revokeObjectURL(preview);
      });
    };
  }, [accountDeletePreviews]);

  const userRows = useMemo(() => {
    const rows = new Map();

    restaurants.forEach((restaurant) => {
      const assignedEmail = (restaurant.store_owner_email || '').trim().toLowerCase();
      if (assignedEmail) {
        const current = rows.get(assignedEmail) || { email: assignedEmail, assigned: [], pending: [] };
        current.assigned.push(restaurant.name || restaurant.slug || restaurant.id);
        rows.set(assignedEmail, current);
      }

      const pendingEmail = (restaurant.pending_store_owner_email || '').trim().toLowerCase();
      if (pendingEmail) {
        const current = rows.get(pendingEmail) || { email: pendingEmail, assigned: [], pending: [] };
        current.pending.push(restaurant.name || restaurant.slug || restaurant.id);
        rows.set(pendingEmail, current);
      }
    });

    return Array.from(rows.values()).sort((a, b) => a.email.localeCompare(b.email));
  }, [restaurants]);

  const stats = useMemo(() => ([
    { label: 'Total stores', value: restaurants.length },
    { label: 'Assigned owners', value: assignedOwners.length },
    { label: 'Pending invitations', value: pendingInvitations.length },
    { label: 'Pending requests', value: totalPendingRequests },
    { label: 'Open messages', value: openMessages.length },
    { label: 'Unassigned stores', value: restaurants.filter((restaurant) => !restaurant.store_owner_email && !restaurant.pending_store_owner_email).length },
  ]), [assignedOwners.length, openMessages.length, pendingInvitations.length, restaurants, totalPendingRequests]);

  const headerActions = isAdmin
    ? [
        { to: '/dashboard/requests', label: 'Requests', count: totalPendingRequests },
        { to: '/dashboard/invitations', label: 'Invitations', count: pendingInvitations.length },
        { to: '/dashboard/messages', label: 'Messages', count: openMessages.length },
      ]
    : [];

  const quickActions = useMemo(() => {
    const baseActions = isAdmin
      ? [
          { key: 'overview', label: 'Overview', description: 'Platform posture, counts, and live signals.', to: '/dashboard/overview' },
          { key: 'stores', label: 'Stores', description: 'Assigned stores and embedded store tools.', to: '/dashboard' },
          { key: 'site-content', label: 'Public Site', description: 'Edit the public marketing pages and branding.', to: '/dashboard/site-content' },
          { key: 'users', label: 'Users', description: 'Owner relationships and pending invitations.', to: '/dashboard/users' },
          { key: 'requests', label: 'Requests', description: 'Review store and account deletion requests.', to: '/dashboard/requests', count: totalPendingRequests },
          { key: 'invitations', label: 'Invitations', description: 'Check owner invites awaiting confirmation.', to: '/dashboard/invitations', count: pendingInvitations.length },
          { key: 'messages', label: 'Messages', description: 'Open the tracked support inbox.', to: '/dashboard/messages', count: openMessages.length },
        ]
      : [
          { key: 'stores', label: 'Stores', description: 'Assigned stores and editor access.', to: '/dashboard' },
          { key: 'messages', label: 'Messages', description: 'View or send messages to Servsolo Admin.', to: '/dashboard/messages', count: openMessages.length },
          { key: 'account', label: 'Account', description: 'Request account changes and review account tickets.', to: '/dashboard/account' },
        ];

    const normalizedQuery = deferredCommandQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return baseActions;
    }

    return baseActions.filter((action) => {
      const haystack = `${action.label} ${action.description}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [deferredCommandQuery, isAdmin, openMessages.length, pendingInvitations.length, totalPendingRequests]);

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshRestaurants = async () => {
    const restaurantsQuery = isAdmin
      ? supabase.from('restaurants').select('*').order('created_at', { ascending: false })
      : supabase.from('restaurants').select('*').eq('store_owner_id', user.id).eq('lifecycle_status', 'active').order('created_at', { ascending: false });

    const { data, error: restaurantsError } = await restaurantsQuery;
    if (restaurantsError) throw restaurantsError;
    setRestaurants(data || []);
  };

  const handleCloseStore = async (restaurant) => {
    if (!restaurant?.id) return;
    if (!window.confirm(`Close store "${restaurant.name || 'Untitled Store'}"?`)) return;

    const reason = window.prompt('Optional reason for closing (visible in history):', '') || '';
    setStoreActionId(restaurant.id);
    setError('');
    setNotice('');
    try {
      await adminCloseStoreLifecycle(restaurant.id, reason.trim());
      await refreshRestaurants();
      setNotice('Store closed successfully.');
    } catch (closeError) {
      setError(closeError.message || 'Failed to close store.');
    } finally {
      setStoreActionId('');
    }
  };

  const handleReactivateStore = async (restaurant) => {
    if (!restaurant?.id) return;
    if (!window.confirm(`Reactivate store "${restaurant.name || 'Untitled Store'}"?`)) return;

    setStoreActionId(restaurant.id);
    setError('');
    setNotice('');
    try {
      await adminReactivateStoreLifecycle(restaurant.id);
      await refreshRestaurants();
      setNotice('Store reactivated successfully.');
    } catch (reactivateError) {
      setError(reactivateError.message || 'Failed to reactivate store.');
    } finally {
      setStoreActionId('');
    }
  };

  const handleReassignStore = async (restaurant) => {
    if (!restaurant?.id) return;

    const ownerEmail = window.prompt(
      `Enter the new owner email for "${restaurant.name || 'Untitled Store'}":`,
      restaurant.pending_store_owner_email || restaurant.store_owner_email || ''
    );
    if (!ownerEmail) return;

    setStoreActionId(restaurant.id);
    setError('');
    setNotice('');
    try {
      await adminReassignStoreOwner(restaurant.id, ownerEmail, new Date().toISOString());
      await refreshRestaurants();
      setNotice('Replacement invitation sent.');
    } catch (reassignError) {
      setError(reassignError.message || 'Failed to reassign store owner.');
    } finally {
      setStoreActionId('');
    }
  };

  const toggleStoreHistory = async (restaurantId) => {
    if (!restaurantId) return;
    if (expandedStoreHistoryId === restaurantId) {
      setExpandedStoreHistoryId(null);
      return;
    }

    setExpandedStoreHistoryId(restaurantId);
    if (storeHistoryById[restaurantId]) {
      return;
    }

    try {
      const result = await adminGetStoreLifecycleHistory(restaurantId);
      setStoreHistoryById((current) => ({
        ...current,
        [restaurantId]: Array.isArray(result?.events) ? result.events : [],
      }));
    } catch (historyError) {
      setError(historyError.message || 'Failed to load store history.');
    }
  };

  const handleQuickAction = (target) => {
    setCommandOpen(false);
    navigate(target);
  };

  const toggleChrome = () => {
    setChromeHidden((current) => !current);
  };

  const currentRestaurant = useMemo(
    () => (restaurantId ? restaurants.find((r) => r.id === restaurantId) : null),
    [restaurants, restaurantId]
  );

  const navItems = useMemo(() => {
    if (isAdmin) {
      const siteUrl = currentRestaurant?.slug ? getStoreOnlineUrl(currentRestaurant.slug) : '';
      return [
        { to: '/dashboard/overview', label: 'Overview' },
        { to: '/dashboard', label: 'Stores' },
        { key: 'site-content', label: 'Public Site', to: '/dashboard/site-content' },
        ...(page === 'store-editor' && restaurantId ? [{
          key: 'store-website',
          label: 'Store Website',
          submenu: [
            ...(siteUrl ? [{ key: 'live-view', label: 'Live View', action: () => window.open(siteUrl, '_blank') }] : []),
            { key: 'general-settings', label: 'General Settings', to: `/dashboard/stores/${restaurantId}/website` },
            { key: 'page-settings', label: 'Page Settings', action: () => window.open(`/dashboard/stores/${restaurantId}/website?fullscreen=1&page=home`, '_blank') },
          ],
        }] : []),
        { to: '/dashboard/users', label: 'Users' },
      ];
    }

    // Store owner — inside a specific store
    if (page === 'store-editor' && restaurantId) {
      const siteUrl = currentRestaurant?.slug ? getStoreOnlineUrl(currentRestaurant.slug) : '';
      return [
        {
          key: 'store',
          label: 'Store',
          submenu: [
            { key: 'admin', label: 'Admin', to: `/dashboard/stores/${restaurantId}/admin` },
            { key: 'store-settings', label: 'Store Settings', to: `/dashboard/stores/${restaurantId}/store` },
          ],
        },
        {
          key: 'products',
          label: 'Products',
          submenu: [
            { key: 'products', label: 'Products', to: `/dashboard/stores/${restaurantId}/products` },
            { key: 'modifiers', label: 'Modifiers', to: `/dashboard/stores/${restaurantId}/modifiers` },
            { key: 'style', label: 'Style', to: `/dashboard/stores/${restaurantId}/style` },
          ],
        },
        {
          key: 'website',
          label: 'Website',
          submenu: [
            ...(siteUrl ? [{ key: 'live-view', label: 'Live View', action: () => window.open(siteUrl, '_blank') }] : []),
            { key: 'site-settings', label: 'Site Settings', to: `/dashboard/stores/${restaurantId}/website` },
            { key: 'page-editor', label: 'Page Editor', action: () => window.open(`/dashboard/stores/${restaurantId}/website?fullscreen=1&page=home`, '_blank') },
          ],
        },
        { key: 'staff', to: `/dashboard/stores/${restaurantId}/staff`, label: 'Staff' },
        { key: 'messages', to: '/dashboard/messages', label: 'Messages', count: openMessages.length },
        {
          key: 'account',
          label: 'Account',
          submenu: [
            { key: 'my-stores', label: 'My Stores', to: '/dashboard' },
            { key: 'account-settings', label: 'Account Settings', to: '/dashboard/account' },
            { key: 'sign-out', label: 'Sign Out', action: handleSignOut },
          ],
        },
      ];
    }

    // Store owner — top-level pages (stores list, messages, account)
    return [
      { to: '/dashboard', label: 'My Stores' },
      { to: '/dashboard/messages', label: 'Messages', count: openMessages.length },
      {
        key: 'account',
        label: 'Account',
        submenu: [
          { key: 'account-settings', label: 'Account Settings', to: '/dashboard/account' },
          { key: 'sign-out', label: 'Sign Out', action: handleSignOut },
        ],
      },
    ];
  }, [isAdmin, page, restaurantId, currentRestaurant, openMessages.length, handleSignOut]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitAccountDeletionRequest = async () => {
    setError('');
    setNotice('');

    if (!window.confirm('Are you sure to delete your account, this cannot be undone once approved ?')) {
      return;
    }

    const provider = String(user?.app_metadata?.provider || 'email').toLowerCase();
    const isGoogleProvider = provider === 'google';
    const name = String(user?.user_metadata?.name || user?.user_metadata?.full_name || user?.email || 'User').trim();
    const email = String(user?.email || '').trim().toLowerCase();
    const details = String(accountDeleteDetails || '').trim();

    if (!email) {
      setError('Your account email is missing. Please sign in again.');
      return;
    }

    if (!isGoogleProvider) {
      const password = window.prompt('Enter your password to continue with account deletion request:', '');
      if (!password) {
        setError('Password confirmation is required to submit account deletion request.');
        return;
      }

      const { error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) {
        setError(authError.message || 'Password verification failed.');
        return;
      }
    }

    const bodyLines = [
      'User requested account deletion.',
      details ? `User note: ${details}` : '',
    ].filter(Boolean);
    const body = bodyLines.join('\n');

    setAccountSubmitting(true);
    try {
      const uploadedAttachments = accountDeleteFiles.length > 0
        ? await Promise.all(accountDeleteFiles.map((file) => uploadSupportAttachment(file)))
        : [];

      const created = await submitSupportMessage({
        created_by_user_id: user?.id || null,
        from_name: name,
        from_email: email,
        subject: 'Account Deletion Request',
        message: serializeSupportMessageContent(body, uploadedAttachments),
        source: 'account-deletion',
      });

      try {
        await notifyAdmin({
          kind: 'account-deletion-request',
          email,
          name,
          subject: 'Account Deletion Request',
          message: body,
          metadata: {
            source: 'account-deletion',
            messageId: created?.id || null,
            requestType: 'delete_account',
            attachmentCount: uploadedAttachments.length,
          },
        });
      } catch {
        // Request is stored even if the email notification fails.
      }

      await refreshMessages();
      setAccountDeleteDetails('');
      setAccountDeleteFiles([]);
      setAccountDeletePreviews((current) => {
        current.forEach((preview) => {
          if (preview) window.URL.revokeObjectURL(preview);
        });
        return [];
      });
      setNotice('Account deletion request submitted. Servsolo admin will review this request before deletion.');
    } catch (submitError) {
      setError(submitError.message || 'Failed to submit account deletion request.');
    } finally {
      setAccountSubmitting(false);
    }
  };

  const handleToggleDeletionStatus = async (requestId, nextStatus) => {
    setMessageActionId(requestId);
    setError('');
    setNotice('');
    try {
      const { error: updateError } = await supabase
        .from('support_messages')
        .update({
          status: nextStatus,
          resolved_at: nextStatus === 'resolved' ? new Date().toISOString() : null,
          archived_at: nextStatus === 'archived' ? new Date().toISOString() : null,
        })
        .eq('id', requestId);
      if (updateError) throw updateError;

      await refreshMessages();
      setNotice('Account deletion request status updated.');
    } catch (statusError) {
      setError(statusError.message || 'Failed to update account deletion request status.');
    } finally {
      setMessageActionId('');
    }
  };

  const handleApproveDeletionRequest = async (request) => {
    if (!request?.id) return;

    setMessageActionId(request.id);
    setError('');
    setNotice('');
    try {
      const approveMessage = String(accountDecisionDrafts[request.id]?.approve || '').trim();
      if (approveMessage) {
        await adminReplySupportMessage(request.id, {
          reply: approveMessage,
          attachments: [],
        });
      }

      if (request.created_by_user_id) {
        await adminDeleteUser(request.created_by_user_id);
      }

      const { error: resolveError } = await supabase
        .from('support_messages')
        .update({
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          archived_at: null,
        })
        .eq('id', request.id);
      if (resolveError) throw resolveError;

      await refreshMessages();
      setNotice('Account deletion approved. User access has been removed.');
    } catch (approveError) {
      setError(approveError.message || 'Failed to approve account deletion request.');
    } finally {
      setMessageActionId('');
    }
  };

  const handleRejectDeletionRequest = async (request) => {
    if (!request?.id) return;

    setMessageActionId(request.id);
    setError('');
    setNotice('');
    try {
      const rejectMessage = String(accountDecisionDrafts[request.id]?.reject || '').trim();
      const retryInstruction = 'Please submit a new account deletion request after completing the information requested by Servsolo Admin.';
      const rejectionReply = rejectMessage
        ? `${rejectMessage}\n\n${retryInstruction}`
        : `Your account deletion request was not approved yet.\n\n${retryInstruction}`;

      await adminReplySupportMessage(request.id, {
        reply: rejectionReply,
        attachments: [],
      });

      const { error: archiveError } = await supabase
        .from('support_messages')
        .update({
          status: 'archived',
          archived_at: new Date().toISOString(),
          resolved_at: null,
        })
        .eq('id', request.id);
      if (archiveError) throw archiveError;

      await refreshMessages();
      setNotice('Account deletion request rejected.');
    } catch (rejectError) {
      setError(rejectError.message || 'Failed to reject account deletion request.');
    } finally {
      setMessageActionId('');
    }
  };

  const handleApproveRequest = async (requestId) => {
    setRequestActionId(requestId);
    const req = requests.find((r) => r.id === requestId);
    try {
      await adminApproveStoreAccessRequest(requestId);
      if (req?.email) {
        notifyStoreRequestDecision({ email: req.email, firstName: req.first_name, storeName: req.store_name, decision: 'approved' }).catch(() => {});
      }
      const { data: restaurantRows } = await supabase.from('restaurants').select('*').order('created_at', { ascending: false });
      const { data: requestRows } = await supabase.from('store_access_requests').select('*').order('requested_at', { ascending: false });
      setRestaurants(restaurantRows || []);
      setRequests(requestRows || []);
    } catch (approveError) {
      setError(approveError.message || 'Failed to approve request.');
    } finally {
      setRequestActionId('');
    }
  };

  const handleRejectRequest = async (requestId) => {
    setRequestActionId(requestId);
    const req = requests.find((r) => r.id === requestId);
    try {
      await adminRejectStoreAccessRequest(requestId);
      if (req?.email) {
        notifyStoreRequestDecision({ email: req.email, firstName: req.first_name, storeName: req.store_name, decision: 'rejected' }).catch(() => {});
      }
      const { data: requestRows } = await supabase.from('store_access_requests').select('*').order('requested_at', { ascending: false });
      setRequests(requestRows || []);
    } catch (rejectError) {
      setError(rejectError.message || 'Failed to reject request.');
    } finally {
      setRequestActionId('');
    }
  };

  async function refreshMessages() {
    const { data: messageRows, error: messageError } = await supabase
      .from('support_messages')
      .select('*')
      .order('created_at', { ascending: false });

    if (messageError) throw messageError;
    setMessages(messageRows || []);
  }

  const handleMessageStatusChange = async (messageId, status) => {
    setMessageActionId(messageId);
    setError('');
    setNotice('');
    try {
      const { error: updateError } = await supabase
        .from('support_messages')
        .update({
          status,
          resolved_at: status === 'resolved' ? new Date().toISOString() : null,
          archived_at: status === 'archived' ? new Date().toISOString() : null,
        })
        .eq('id', messageId);

      if (updateError) throw updateError;

      await refreshMessages();
      if (status === 'archived') {
        setNotice('Message archived.');
      }
    } catch (messageError) {
      setError(messageError.message || 'Failed to update message status.');
    } finally {
      setMessageActionId('');
    }
  };

  const MAX_REPLY_FILES = 10;
  const MAX_ACCOUNT_DELETE_FILES = 10;

  const handleMessageReplyChange = (messageId, value) => {
    setMessageReplyDrafts((current) => ({ ...current, [messageId]: value }));
  };

  const handleMessageReplyFilesAdd = (messageId, selected) => {
    const current = messageReplyFiles[messageId] || [];
    const merged = [...current, ...Array.from(selected)].slice(0, MAX_REPLY_FILES);
    const previews = merged.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
    setMessageReplyFiles(c => ({ ...c, [messageId]: merged }));
    setMessageReplyPreviews(c => ({ ...c, [messageId]: previews }));
  };

  const handleMessageReplyFileRemove = (messageId, index) => {
    const next = (messageReplyFiles[messageId] || []).filter((_, i) => i !== index);
    const previews = next.map(f => f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
    setMessageReplyFiles(c => ({ ...c, [messageId]: next }));
    setMessageReplyPreviews(c => ({ ...c, [messageId]: previews }));
  };

  const handleAccountDeleteFilesAdd = (selected) => {
    const incoming = Array.from(selected || []);
    if (!incoming.length) return;

    const merged = [...accountDeleteFiles, ...incoming].slice(0, MAX_ACCOUNT_DELETE_FILES);
    setAccountDeleteFiles(merged);
    setAccountDeletePreviews((current) => {
      current.forEach((preview) => {
        if (preview) window.URL.revokeObjectURL(preview);
      });
      return merged.map((file) => (file.type.startsWith('image/') ? window.URL.createObjectURL(file) : null));
    });
  };

  const handleAccountDeleteFileRemove = (index) => {
    const next = accountDeleteFiles.filter((_, fileIndex) => fileIndex !== index);
    setAccountDeleteFiles(next);
    setAccountDeletePreviews((current) => {
      current.forEach((preview) => {
        if (preview) window.URL.revokeObjectURL(preview);
      });
      return next.map((file) => (file.type.startsWith('image/') ? window.URL.createObjectURL(file) : null));
    });
  };

  const handleReplySupportMessage = async (messageId) => {
    const reply = (messageReplyDrafts[messageId] || '').trim();
    if (!reply) {
      setError('Reply cannot be empty.');
      setNotice('');
      return;
    }

    setMessageActionId(messageId);
    setError('');
    setNotice('');
    try {
      const replyFiles = messageReplyFiles[messageId] || [];
      const uploadedAttachments = replyFiles.length > 0
        ? await Promise.all(replyFiles.map(f => uploadSupportAttachment(f)))
        : [];
      const result = await adminReplySupportMessage(messageId, { reply, attachments: uploadedAttachments });
      await refreshMessages();
      setMessageReplyDrafts((current) => ({ ...current, [messageId]: '' }));
      setMessageReplyFiles(c => ({ ...c, [messageId]: [] }));
      setMessageReplyPreviews(c => ({ ...c, [messageId]: [] }));
      if (result?.email?.delivered) {
        setNotice('Reply saved and email sent to the sender.');
      } else if (result?.email?.error) {
        setNotice(`Reply saved. Email error: ${result.email.error}`);
      } else {
        setNotice('Reply saved.');
      }
    } catch (replyError) {
      setError(replyError.message || 'Failed to send reply.');
    } finally {
      setMessageActionId('');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message permanently?')) return;

    setMessageActionId(messageId);
    setError('');
    setNotice('');
    try {
      await adminDeleteSupportMessage(messageId);
      await refreshMessages();
      setNotice('Message deleted.');
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete message.');
    } finally {
      setMessageActionId('');
    }
  };

  const renderAttachments = (attachments) => {
    if (!attachments?.length) return null;
    return (
      <div className="pas-attachment-section">
        <div className="pas-attachment-section-head">
          <span className="pas-attachment-section-title">Attachments</span>
        </div>
        <div className="pas-attachments">
        {attachments.map((a, i) => {
          if (!a?.url) return null;
          const isImage = /\.(png|jpe?g|gif|webp)$/i.test(a.fileName || '') || String(a.contentType || '').startsWith('image/');
          return (
            <div key={i} className="pas-attachment-card">
              {isImage ? (
                <div className="pas-attachment-thumb-wrap">
                  <a href={a.url} target="_blank" rel="noreferrer" className="pas-attachment-thumb-link">
                    <img src={a.url} alt={a.fileName || 'Attachment preview'} className="pas-attachment-preview" />
                  </a>
                  <button
                    type="button"
                    className="pas-attachment-download-icon"
                    aria-label={`Download ${a.fileName || 'attachment'}`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      downloadAttachment(a);
                    }}
                  >
                    ↓
                  </button>
                </div>
              ) : (
                <a href={a.url} target="_blank" rel="noreferrer" className="pas-attachment-file-tile">
                  {(a.fileName || 'FILE').split('.').pop()?.toUpperCase() || 'FILE'}
                </a>
              )}
              <div className="pas-attachment-meta">
                <div className="pas-attachment-name" title={a.fileName || 'Attachment'}>{a.fileName || 'Attachment'}</div>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    );
  };

  const renderPage = () => {
    if (loading) {
      return <div className="platform-admin-state">Loading Servsolo admin data...</div>;
    }

    if (error) {
      return <div className="platform-admin-state error">{error}</div>;
    }

    if (page === 'stores') {
      return (
        <Dashboard
          user={user}
          isAdmin={isAdmin}
          role={role}
          showSessionRow={false}
          showBrandHeader={false}
          shellBasePath="/dashboard"
          defaultStoreSection="admin"
        />
      );
    }

    if (page === 'restaurants') {
      return (
        <section className="platform-admin-section">
          <div className="platform-admin-section-header">
            <h2>Restaurants</h2>
            <p>All tenant stores with owner and invitation state in one place.</p>
          </div>
          {notice && <div className="platform-admin-inline-note platform-admin-inline-note-success">{notice}</div>}
          <div className="platform-admin-table-wrap">
            <table className="platform-admin-table">
              <thead>
                <tr>
                  <th>Store</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Assigned owner</th>
                  <th>Pending invite</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {restaurants.map((restaurant) => {
                  const status = getLifecycleStatus(restaurant);
                  const historyItems = storeHistoryById[restaurant.id] || [];
                  const isBusy = storeActionId === restaurant.id;
                  const isHistoryOpen = expandedStoreHistoryId === restaurant.id;

                  return (
                    <React.Fragment key={restaurant.id}>
                      <tr>
                        <td>{restaurant.name || 'Untitled Store'}</td>
                        <td>{restaurant.slug ? getStoreHost(restaurant.slug) : 'No slug'}</td>
                        <td>
                          <span className={`platform-admin-lifecycle-badge ${status === 'closed' ? 'closed' : 'active'}`}>
                            {status}
                          </span>
                        </td>
                        <td>{restaurant.store_owner_email || 'Unassigned'}</td>
                        <td>{restaurant.pending_store_owner_email || 'None'}</td>
                        <td>
                          <div className="platform-admin-row-actions">
                            {status === 'closed' ? (
                              <button
                                type="button"
                                className="platform-admin-table-btn"
                                disabled={isBusy}
                                onClick={() => handleReactivateStore(restaurant)}
                              >
                                {isBusy ? 'Working...' : 'Reactivate'}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="platform-admin-table-btn danger"
                                disabled={isBusy}
                                onClick={() => handleCloseStore(restaurant)}
                              >
                                {isBusy ? 'Working...' : 'Close'}
                              </button>
                            )}
                            <button
                              type="button"
                              className="platform-admin-table-btn"
                              disabled={isBusy}
                              onClick={() => handleReassignStore(restaurant)}
                            >
                              Reassign Owner
                            </button>
                            <button
                              type="button"
                              className="platform-admin-table-btn secondary"
                              onClick={() => toggleStoreHistory(restaurant.id)}
                            >
                              {isHistoryOpen ? 'Hide History' : 'History'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isHistoryOpen && (
                        <tr className="platform-admin-store-history-row">
                          <td colSpan={6}>
                            {historyItems.length === 0 ? (
                              <div className="platform-admin-history-empty">No lifecycle events yet.</div>
                            ) : (
                              <div className="platform-admin-store-history-list">
                                {historyItems.map((event) => (
                                  <div key={event.id} className="platform-admin-store-history-item">
                                    <strong>{event.event_type.replace(/_/g, ' ')}</strong>
                                    <span>{fmtDate(event.created_at)}</span>
                                    <span>
                                      {event.from_owner_email || 'No previous owner'}
                                      {' -> '}
                                      {event.to_owner_email || 'No new owner'}
                                    </span>
                                    {event.note && <span>{event.note}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      );
    }

    if (page === 'store-editor') {
      if (storeSection === 'website') {
        return (
          <StoreSiteManager
            restaurantId={restaurantId}
            restaurantSlug={currentRestaurant?.slug || ''}
            restaurantName={currentRestaurant?.name || ''}
          />
        );
      }

      if (storeSection === 'staff') {
        return (
          <section className="platform-admin-section">
            <StaffPage restaurantId={restaurantId} />
          </section>
        );
      }

      if (storeSection === 'style') {
        return (
          <section className="platform-admin-section">
            <StyleSettingsPage restaurantId={restaurantId} />
          </section>
        );
      }

      const sectionConfig = STORE_SECTION_CONFIG[storeSection] || STORE_SECTION_CONFIG.products;

      if (chromeHidden) {
        return (
          <LegacyRestaurantPage
            fileName={sectionConfig.fileName}
            title={sectionConfig.title}
            session={session}
            isAdmin={isAdmin}
            routeBasePrefix="/dashboard/stores"
            embedded={false}
            fullscreen
          />
        );
      }

      return (
        <section className="platform-admin-section platform-admin-store-editor">
          <div className="platform-admin-section-header">
            <h2>{sectionConfig.title}</h2>
            <p>Manage this store inside the Servsolo workspace shell.</p>
          </div>
          <LegacyRestaurantPage
            fileName={sectionConfig.fileName}
            title={sectionConfig.title}
            session={session}
            isAdmin={isAdmin}
            routeBasePrefix="/dashboard/stores"
            embedded
            fullscreen={false}
          />
        </section>
      );
    }

    if (page === 'users') {
      return (
        <section className="platform-admin-section">
          <div className="platform-admin-section-header">
            <h2>Customer Accounts</h2>
            <p>A derived view of active owner relationships and pending invitation targets.</p>
          </div>
          <div className="platform-admin-card-grid">
            {userRows.map((row) => (
              <article key={row.email} className="platform-admin-user-card">
                <h3>{row.email}</h3>
                <div>Assigned stores: {row.assigned.length || 0}</div>
                <div>Pending invitations: {row.pending.length || 0}</div>
                {row.assigned.length > 0 && <p>Assigned: {row.assigned.join(', ')}</p>}
                {row.pending.length > 0 && <p>Pending: {row.pending.join(', ')}</p>}
              </article>
            ))}
          </div>
        </section>
      );
    }

    if (page === 'invitations') {
      return (
        <section className="platform-admin-section">
          <div className="platform-admin-section-header">
            <h2>Invitations</h2>
            <p>Current owner invitations awaiting confirmation.</p>
          </div>
          <div className="platform-admin-card-grid">
            {pendingInvitations.length === 0 ? (
              <div className="platform-admin-empty">No pending invitations right now.</div>
            ) : pendingInvitations.map((restaurant) => (
              <article key={restaurant.id} className="platform-admin-invite-card">
                <span>Awaiting confirmation</span>
                <h3>{restaurant.name || 'Untitled Store'}</h3>
                <p>{restaurant.pending_store_owner_email}</p>
                <strong>{restaurant.pending_store_owner_invited_at ? new Date(restaurant.pending_store_owner_invited_at).toLocaleString() : 'Recently sent'}</strong>
              </article>
            ))}
          </div>
        </section>
      );
    }

    if (page === 'requests') {
      return (
        <section className="platform-admin-section">
          <div className="platform-admin-section-header">
            <h2>Store Requests</h2>
            <p>Review first-store requests from new or unassigned users.</p>
          </div>
          <div className="platform-admin-thread-list">
            {pendingRequests.length === 0 ? (
              <div className="platform-admin-empty">No pending store requests right now.</div>
            ) : pendingRequests.map((request) => (
              <article key={request.id} className="platform-admin-request-card">
                <button type="button" className="platform-admin-thread-toggle" onClick={() => setExpandedRequestId((current) => current === request.id ? null : request.id)}>
                  <div>
                    <span>Pending request</span>
                    <h3>{request.store_name}</h3>
                    <p>{request.first_name} {request.last_name}</p>
                  </div>
                  <strong>{request.requested_at ? new Date(request.requested_at).toLocaleString() : 'Recently submitted'}</strong>
                </button>
                {expandedRequestId === request.id && (
                  <div className="platform-admin-thread-body">
                    <p>{request.email}</p>
                    <p>{request.phone}</p>
                    <p>{request.industry || 'No industry selected'}</p>
                    <div className="platform-admin-request-actions">
                      <button disabled={requestActionId === request.id} onClick={() => handleApproveRequest(request.id)}>
                        {requestActionId === request.id ? 'Working...' : 'Approve'}
                      </button>
                      <button className="secondary" disabled={requestActionId === request.id} onClick={() => handleRejectRequest(request.id)}>
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>

          <div className="platform-admin-section-header" style={{ marginTop: 24 }}>
            <h2>Account Deletion Requests</h2>
            <p>Review and process user account deletion submissions.</p>
          </div>
          <div className="platform-admin-thread-list">
            {adminAccountDeletionRequests.length === 0 ? (
              <div className="platform-admin-empty">No account deletion requests right now.</div>
            ) : adminAccountDeletionRequests.map((request) => (
              <article key={request.id} className="platform-admin-request-card">
                <button
                  type="button"
                  className="platform-admin-thread-toggle"
                  onClick={() => setExpandedRequestId((current) => current === request.id ? null : request.id)}
                >
                  <div>
                    <span>Account deletion</span>
                    <h3>{request.from_name || request.from_email || 'Unknown user'}</h3>
                    <p>{request.from_email}</p>
                  </div>
                  <strong>{request.created_at ? new Date(request.created_at).toLocaleString() : 'Recently submitted'}</strong>
                </button>
                {expandedRequestId === request.id && (
                  <div className="platform-admin-thread-body">
                    <p>Status: {(request.status || 'open').replace('_', ' ')}</p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{sanitizeSupportMessageText(parseSupportMessageContent(request.message || '').text) || 'No details provided.'}</p>
                    <div className="platform-admin-request-actions">
                      <button
                        disabled={messageActionId === request.id}
                        onClick={() => handleToggleDeletionStatus(request.id, request.status === 'in_progress' ? 'open' : 'in_progress')}
                      >
                        {messageActionId === request.id ? 'Working...' : request.status === 'in_progress' ? 'Unmark Progress' : 'Mark In Progress'}
                      </button>
                      <button
                        className="secondary"
                        disabled={messageActionId === request.id}
                        onClick={() => handleToggleDeletionStatus(request.id, request.status === 'resolved' ? 'open' : 'resolved')}
                      >
                        {request.status === 'resolved' ? 'Unmark Resolved' : 'Mark Resolved'}
                      </button>
                    </div>
                    <div className="platform-admin-request-actions" style={{ marginTop: 10 }}>
                      <div className="platform-admin-decision-block">
                        <label>Approve message (optional)</label>
                        <textarea
                          rows={3}
                          value={accountDecisionDrafts[request.id]?.approve || ''}
                          onChange={(event) => setAccountDecisionDrafts((current) => ({
                            ...current,
                            [request.id]: {
                              ...(current[request.id] || {}),
                              approve: event.target.value,
                            },
                          }))}
                          placeholder="Optional message to user before account deletion."
                        />
                        <button
                          type="button"
                          disabled={messageActionId === request.id}
                          onClick={() => handleApproveDeletionRequest(request)}
                        >
                          {messageActionId === request.id ? 'Working...' : 'Approve'}
                        </button>
                      </div>
                      <div className="platform-admin-decision-block">
                        <label>Reject reason (optional)</label>
                        <textarea
                          rows={3}
                          value={accountDecisionDrafts[request.id]?.reject || ''}
                          onChange={(event) => setAccountDecisionDrafts((current) => ({
                            ...current,
                            [request.id]: {
                              ...(current[request.id] || {}),
                              reject: event.target.value,
                            },
                          }))}
                          placeholder="Optional rejection reason for the user."
                        />
                        <button
                          type="button"
                          className="secondary"
                          disabled={messageActionId === request.id}
                          onClick={() => handleRejectDeletionRequest(request)}
                        >
                          {messageActionId === request.id ? 'Working...' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>

          <div className="platform-admin-section-header" style={{ marginTop: 24 }}>
            <button
              type="button"
              className="platform-admin-completed-toggle"
              onClick={() => setCompletedDeletionSectionOpen((current) => !current)}
            >
              <span>Completed Account Deletion</span>
              <strong>{completedDeletionSectionOpen ? 'Hide' : 'Show'}</strong>
            </button>
            <p>Previously processed requests with decision results and processing timestamps.</p>
          </div>
          {completedDeletionSectionOpen && (
            <div className="platform-admin-thread-list">
              {completedAccountDeletionRequests.length === 0 ? (
                <div className="platform-admin-empty">No completed account deletion requests yet.</div>
              ) : completedAccountDeletionRequests.map((request) => {
                const processedAt = request.status === 'resolved' ? request.resolved_at : request.archived_at;
                const isRejected = request.status === 'archived';
                const resultLabel = isRejected ? 'Rejected' : 'Approved';
                const parsedMessage = parseSupportMessageContent(request.message || '');
                const parsedReply = parseSupportMessageContent(request.admin_reply || '');

                return (
                  <article key={request.id} className="platform-admin-completed-row-card">
                    <div className="platform-admin-completed-row-inline">
                      <span className="platform-admin-completed-cell">
                        <strong>User</strong>
                        {request.from_name || 'Unknown user'}
                      </span>
                      <span className="platform-admin-completed-cell">
                        <strong>Email</strong>
                        {request.from_email || 'Unknown'}
                      </span>
                      <span className="platform-admin-completed-cell">
                        <strong>Request time</strong>
                        {fmtDate(request.created_at) || 'Unknown'}
                      </span>
                      <span className="platform-admin-completed-cell">
                        <strong>Status</strong>
                        <span className={isRejected ? 'platform-admin-completed-status rejected' : 'platform-admin-completed-status approved'}>
                          {resultLabel}
                        </span>
                      </span>
                      <span className="platform-admin-completed-cell">
                        <strong>Completion time</strong>
                        {processedAt ? new Date(processedAt).toLocaleString() : 'Unknown'}
                      </span>
                      <button
                        type="button"
                        className="platform-admin-completed-see-more"
                        onClick={() => setExpandedCompletedRequestId((current) => current === request.id ? null : request.id)}
                      >
                        {expandedCompletedRequestId === request.id ? 'Hide' : 'See more'}
                      </button>
                    </div>

                    {expandedCompletedRequestId === request.id && (
                      <div className="platform-admin-thread-body platform-admin-completed-detail">
                        {isRejected && <div className="platform-admin-account-rejected-status">Status: Rejected</div>}
                        <p style={{ whiteSpace: 'pre-wrap' }}>{sanitizeSupportMessageText(parsedMessage.text) || 'No details provided.'}</p>
                        {!!parsedReply.text && (
                          <div className="platform-admin-account-admin-reply">
                            <strong>Admin response</strong>
                            <p>{sanitizeSupportMessageText(parsedReply.text)}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      );
    }

    if (page === 'messages') {
      // ── Bulk action helpers ─────────────────────────────────────────────
      const allVisibleIds = inboxMessages.map(m => m.id);
      const allChecked = allVisibleIds.length > 0 && allVisibleIds.every(id => checkedIds.has(id));
      const someChecked = checkedIds.size > 0;

      const toggleCheck = (id) => setCheckedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
      const toggleAll = () => setCheckedIds(allChecked ? new Set() : new Set(allVisibleIds));

      const bulkAction = async (action) => {
        const ids = [...checkedIds];
        if (!ids.length) return;
        if (action === 'delete' && !window.confirm(`Delete ${ids.length} message(s)?`)) return;
        setError(''); setNotice('');
        try {
          if (action === 'delete') {
            await Promise.all(ids.map(id => adminDeleteSupportMessage(id)));
            setNotice(`${ids.length} message(s) deleted.`);
          } else {
            const statusMap = { archive: 'archived', resolve: 'resolved', open: 'open' };
            const status = statusMap[action];
            await Promise.all(ids.map(id =>
              supabase.from('support_messages').update({
                status,
                archived_at: status === 'archived' ? new Date().toISOString() : null,
                resolved_at: status === 'resolved' ? new Date().toISOString() : null,
              }).eq('id', id)
            ));
            setNotice(`${ids.length} message(s) marked ${status}.`);
          }
          setCheckedIds(new Set());
          await refreshMessages();
        } catch (err) {
          setError(err.message || 'Bulk action failed.');
        }
      };

      return (
        <div className="pas-inbox-layout">
          {/* ── Left sidebar: inbox list ── */}
          {isAdmin && (
            <aside className="pas-inbox-sidebar">
              <div className="pas-inbox-title">Inbox</div>
              <button
                className={`pas-inbox-item${selectedInbox === 'all' ? ' active' : ''}`}
                onClick={() => { setSelectedInbox('all'); setCheckedIds(new Set()); setExpandedMessageId(null); }}
              >
                <span>All Messages</span>
                <span className="pas-inbox-count">{messages.filter(m => m.status !== 'archived').length}</span>
              </button>
              {(inboxGroups.users || []).map(g => (
                <button
                  key={g.email}
                  className={`pas-inbox-item${selectedInbox === g.email ? ' active' : ''}`}
                  onClick={() => { setSelectedInbox(g.email); setCheckedIds(new Set()); setExpandedMessageId(null); }}
                >
                  <span className="pas-inbox-name">{g.name}</span>
                  <span className="pas-inbox-count">{g.messages.length}</span>
                </button>
              ))}
              {(inboxGroups.requests || []).length > 0 && (
                <button
                  className={`pas-inbox-item${selectedInbox === 'requests' ? ' active' : ''}`}
                  onClick={() => { setSelectedInbox('requests'); setCheckedIds(new Set()); setExpandedMessageId(null); }}
                >
                  <span>New Requests</span>
                  <span className="pas-inbox-count pas-inbox-count-new">{(inboxGroups.requests || []).length}</span>
                </button>
              )}
              <div className="pas-inbox-divider" />
              <button
                className={`pas-inbox-item${selectedInbox === 'archived' ? ' active' : ''}`}
                onClick={() => { setSelectedInbox('archived'); setCheckedIds(new Set()); setExpandedMessageId(null); }}
              >
                <span>Archived</span>
                <span className="pas-inbox-count">{messages.filter(m => m.status === 'archived').length}</span>
              </button>
            </aside>
          )}

          {/* ── Main panel ── */}
          <div className="pas-inbox-main">
            <div className="pas-section-header">
              <h1>{selectedInbox === 'archived' ? 'Archived' : selectedInbox === 'requests' ? 'New Requests' : selectedInbox === 'all' ? 'All Messages' : (inboxGroups.users || []).find(g => g.email === selectedInbox)?.name || selectedInbox}</h1>
              {!isAdmin && (
                <SupportMessageModal user={user} source="dashboard-shell" triggerLabel="+ New Message" triggerClassName="pas-btn-primary" />
              )}
            </div>
            {notice && <div className="platform-admin-inline-note platform-admin-inline-note-success">{notice}</div>}

            {/* Bulk action bar */}
            {isAdmin && inboxMessages.length > 0 && (
              <div className="pas-bulk-bar">
                <label className="pas-bulk-check-all">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                  {someChecked ? `${checkedIds.size} selected` : 'Select all'}
                </label>
                {someChecked && (
                  <div className="pas-bulk-actions">
                    <button className="pas-bulk-btn" onClick={() => bulkAction('open')}>Mark Open</button>
                    <button className="pas-bulk-btn" onClick={() => bulkAction('resolve')}>Resolve</button>
                    <button className="pas-bulk-btn" onClick={() => bulkAction('archive')}>Archive</button>
                    <button className="pas-bulk-btn pas-bulk-btn-danger" onClick={() => bulkAction('delete')}>Delete</button>
                  </div>
                )}
              </div>
            )}

            {inboxMessages.length === 0 ? (
              <div className="pas-empty"><p>No messages here.</p></div>
            ) : (
            <div className="pas-message-list">
              {inboxMessages.map((message) => {
                const parsedMessage = parseSupportMessageContent(message.message || '');
                const parsedReply = parseSupportMessageContent(message.admin_reply || '');
                const thread = Array.isArray(message.thread_messages) ? message.thread_messages : [];
                const parsedMessageText = sanitizeSupportMessageText(parsedMessage.text);
                const parsedReplyText = sanitizeSupportMessageText(parsedReply.text);
                const allAttachments = collectMessageAttachments(parsedMessage, parsedReply, thread);
                const isOpen = expandedMessageId === message.id;
                return (
                  <div key={message.id} className={`pas-message-card${isOpen ? ' open' : ''}${checkedIds.has(message.id) ? ' checked' : ''}`}>
                    <div className="pas-message-header">
                      {isAdmin && (
                        <input type="checkbox" className="pas-msg-checkbox" checked={checkedIds.has(message.id)}
                          onChange={() => toggleCheck(message.id)} onClick={e => e.stopPropagation()} />
                      )}
                      <div className="pas-message-header-body" onClick={() => setExpandedMessageId(isOpen ? null : message.id)}>
                        <div className="pas-message-subject">{message.subject}</div>
                        <div className="pas-message-meta">
                          {isAdmin && <span className="pas-msg-sender">{message.from_name || message.from_email}</span>}
                          {fmtDate(message.created_at)}
                          <span className={`pas-badge pas-badge-${message.status}`}>{message.status.replace('_', ' ')}</span>
                          {allAttachments.length > 0 && <span className="pas-attachment-indicator" title={`${allAttachments.length} attachment${allAttachments.length === 1 ? '' : 's'}`}>📎 {allAttachments.length}</span>}
                          {message.admin_reply && <span className="pas-replied-dot">● Reply</span>}
                        </div>
                      </div>
                      <span className="pas-chevron" onClick={() => setExpandedMessageId(isOpen ? null : message.id)}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                    {isOpen && (
                      <div className="pas-message-body">
                        {allAttachments.length > 1 && (
                          <div className="pas-thread-actions">
                            <button type="button" className="pas-download-all-btn" onClick={() => downloadAttachments(allAttachments)}>Download all attachments</button>
                          </div>
                        )}
                        {isAdmin && (
                          <div className="pas-message-from">
                            From: <strong>{message.from_name || 'Unknown'}</strong> · {message.from_email}
                          </div>
                        )}
                        {/* Original user message */}
                        <div className="pas-message-bubble pas-bubble-user">
                          <div className="pas-bubble-label">{isAdmin ? (message.from_name || message.from_email || 'User') : 'You'}</div>
                          <p>{parsedMessageText}</p>
                          {renderAttachments(parsedMessage.attachments)}
                        </div>
                        {/* Original admin reply (shown only when no thread yet) */}
                        {message.admin_reply && (Array.isArray(message.thread_messages) ? message.thread_messages : []).length === 0 && (
                          <div className="pas-message-bubble pas-bubble-admin">
                            <div className="pas-bubble-label">Servsolo · {fmtDate(message.admin_replied_at)}</div>
                            <p>{parsedReplyText}</p>
                            {renderAttachments(parsedReply.attachments)}
                          </div>
                        )}
                        {/* Thread messages */}
                        {thread.map((entry, ti) => (
                          <div key={ti} className={`pas-message-bubble ${entry.role === 'admin' ? 'pas-bubble-admin' : 'pas-bubble-user'}`}>
                            <div className="pas-bubble-label">
                              {entry.role === 'admin' ? 'Servsolo' : (message.from_name || message.from_email || 'User')}
                              {entry.created_at ? ` · ${fmtDate(entry.created_at)}` : ''}
                            </div>
                            <p>{sanitizeSupportMessageText(entry.text)}</p>
                            {renderAttachments(entry.attachments)}
                          </div>
                        ))}
                        {!message.admin_reply && thread.filter(t => t.role === 'admin').length === 0 && (
                          <div className="pas-message-awaiting">No reply sent yet.</div>
                        )}
                        {isAdmin && (
                          <div className="pas-admin-controls">
                            <div className="pas-status-row">
                              <label>Status:</label>
                              <select value={message.status} disabled={messageActionId === message.id} onChange={(event) => handleMessageStatusChange(message.id, event.target.value)}>
                                <option value="open">Open</option>
                                <option value="in_progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="archived">Archived</option>
                              </select>
                            </div>
                            <textarea
                              className="pas-reply-input"
                              rows={4}
                              value={messageReplyDrafts[message.id] ?? ''}
                              onChange={(event) => handleMessageReplyChange(message.id, event.target.value)}
                              placeholder="Write a reply to this sender…"
                            />
                            <label className="pas-file-add-btn">
                              + Add files (up to {MAX_REPLY_FILES})
                              <input
                                type="file"
                                multiple
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                style={{ display: 'none' }}
                                disabled={(messageReplyFiles[message.id] || []).length >= MAX_REPLY_FILES}
                                onChange={(e) => handleMessageReplyFilesAdd(message.id, e.target.files)}
                              />
                            </label>
                            {(messageReplyFiles[message.id] || []).length > 0 && (
                              <div className="pas-reply-thumbs">
                                {(messageReplyFiles[message.id] || []).map((f, i) => (
                                  <div key={i} className="pas-reply-thumb">
                                    {(messageReplyPreviews[message.id] || [])[i]
                                      ? <img src={(messageReplyPreviews[message.id] || [])[i]} alt={f.name} />
                                      : <div className="pas-reply-thumb-file">{f.name.split('.').pop().toUpperCase()}</div>
                                    }
                                    <button type="button" className="pas-reply-thumb-remove" onClick={() => handleMessageReplyFileRemove(message.id, i)}>×</button>
                                    <div className="pas-reply-thumb-name">{f.name}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="pas-actions-row">
                              <button type="button" className="pas-btn-primary" disabled={messageActionId === message.id} onClick={() => handleReplySupportMessage(message.id)}>
                                {messageActionId === message.id ? 'Sending...' : 'Send Reply'}
                              </button>
                              <button type="button" className="pas-btn-danger" disabled={messageActionId === message.id} onClick={() => handleDeleteMessage(message.id)}>
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>
      );
    }

    if (page === 'account') {
      const provider = String(user?.app_metadata?.provider || 'email').toLowerCase();
      const isGoogleProvider = provider === 'google';
      return (
        <section className="platform-admin-section">
          <div className="platform-admin-section-header">
            <h2>Account</h2>
            <p>Request account deletion from Servsolo admin.</p>
          </div>

          {notice && <div className="platform-admin-inline-note platform-admin-inline-note-success">{notice}</div>}

          <div className="platform-admin-account-delete-card">
            <p className="platform-admin-account-delete-text">
              Press the button below to request account deletion.
              {isGoogleProvider
                ? ' Google sign-in users will confirm with the current signed-in session (Google passwords are not collected by Servsolo).'
                : ' Email/password users will be asked for their current password.'}
            </p>
            <label>
              Optional note for admin
              <textarea
                rows={4}
                value={accountDeleteDetails}
                onChange={(event) => setAccountDeleteDetails(event.target.value)}
                placeholder="Optional reason or context for your deletion request."
              />
            </label>
            <div className="platform-admin-account-file-row">
              <label className="pas-file-add-btn">
                + Add files or photos (up to {MAX_ACCOUNT_DELETE_FILES})
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  style={{ display: 'none' }}
                  disabled={accountDeleteFiles.length >= MAX_ACCOUNT_DELETE_FILES || accountSubmitting}
                  onChange={(event) => handleAccountDeleteFilesAdd(event.target.files)}
                />
              </label>
              <span className="platform-admin-account-file-hint">
                Attach screenshots or documents related to your request.
              </span>
            </div>
            {accountDeleteFiles.length > 0 && (
              <div className="pas-reply-thumbs">
                {accountDeleteFiles.map((file, index) => (
                  <div key={`${file.name}-${index}`} className="pas-reply-thumb">
                    {accountDeletePreviews[index]
                      ? <img src={accountDeletePreviews[index]} alt={file.name} />
                      : <div className="pas-reply-thumb-file">{file.name.split('.').pop()?.toUpperCase() || 'FILE'}</div>
                    }
                    <button
                      type="button"
                      className="pas-reply-thumb-remove"
                      onClick={() => handleAccountDeleteFileRemove(index)}
                    >
                      ×
                    </button>
                    <div className="pas-reply-thumb-name">{file.name}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="platform-admin-account-form-actions">
              <button
                type="button"
                className="pas-btn-danger"
                disabled={accountSubmitting}
                onClick={handleSubmitAccountDeletionRequest}
              >
                {accountSubmitting ? 'Submitting...' : 'Delete my account'}
              </button>
            </div>
          </div>

          <div className="platform-admin-account-history">
            <h3>Your Account Deletion Requests</h3>
            {accountRequests.length === 0 ? (
              <div className="platform-admin-history-empty">No account deletion requests submitted yet.</div>
            ) : (
              <div className="platform-admin-store-history-list">
                {accountRequests.map((request) => {
                  const parsedRequest = parseSupportMessageContent(request.message || '');
                  const parsedReply = parseSupportMessageContent(request.admin_reply || '');
                  const parsedRequestText = sanitizeSupportMessageText(parsedRequest.text);
                  const parsedReplyText = sanitizeSupportMessageText(parsedReply.text);
                  const statusLabel = request.status === 'archived'
                    ? 'rejected'
                    : (request.status || 'open').replace('_', ' ');

                  return (
                    <div key={request.id} className="platform-admin-store-history-item">
                      <strong>{request.subject || 'Account deletion request'}</strong>
                      <span>{fmtDate(request.created_at)}</span>
                      {statusLabel !== 'rejected' && <span>Status: {statusLabel}</span>}
                      {!!parsedRequestText && <p className="platform-admin-account-history-note">{parsedRequestText}</p>}
                      {renderAttachments(parsedRequest.attachments)}
                      {!!parsedReplyText && (
                        <>
                          {statusLabel === 'rejected' && (
                            <div className="platform-admin-account-rejected-status">Status: Rejected</div>
                          )}
                          <div className="platform-admin-account-admin-reply">
                          <strong>Admin response</strong>
                          <p>{parsedReplyText}</p>
                          {renderAttachments(parsedReply.attachments)}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      );
    }

    if (page === 'site-content') {
      return isAdmin
        ? <LandingPageEditor />
        : <div className="platform-admin-state error">Only Servsolo Admin can edit the public site.</div>;
    }

    return (
      <>
        <section className="platform-admin-stats-grid">
          {stats.map((stat) => (
            <article key={stat.label} className="platform-admin-stat-card">
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
            </article>
          ))}
        </section>

        <section className="platform-admin-overview-grid">
          <article className="platform-admin-highlight-card">
            <span>Platform posture</span>
            <h2>Public site, operator dashboard, and Servsolo admin now live on one app.</h2>
            <p>Use this area as the internal control layer for restaurants, invitations, user relationships, and operational oversight.</p>
          </article>

          <article className="platform-admin-list-card">
            <span>Pending requests</span>
            <ul>
              {pendingRequests.slice(0, 5).map((request) => (
                <li key={request.id}>
                  <strong>{request.store_name || 'Untitled Request'}</strong>
                  <span>{request.email}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="platform-admin-list-card">
            <span>Open messages</span>
            <ul>
              {openMessages.slice(0, 5).map((message) => (
                <li key={message.id}>
                  <strong>{message.subject || 'Untitled Message'}</strong>
                  <span>{message.from_email}</span>
                </li>
              ))}
            </ul>
          </article>
        </section>
      </>
    );
  };

  return (
    <div className={`platform-admin-shell${chromeHidden ? ' shell-hidden' : ''}${page === 'store-editor' && chromeHidden ? ' shell-focus-store' : ''}${darkMode ? ' dark' : ''}`}>
      {/* Fullscreen toggle — always fixed at top-left corner, except in the dedicated
          fullscreen preview window, which has no chrome to toggle back to. */}
      {!isFullscreenPreview && (
        <button type="button" className="platform-admin-shell-toggle" onClick={toggleChrome} title={chromeHidden ? 'Show navigation' : 'Hide navigation'}>
          {chromeHidden ? '⌟' : '⌜'}
        </button>
      )}

      {/* Sidebar */}
      {!chromeHidden && (
        <aside className="platform-admin-sidebar">
          <div className="platform-admin-nav-scroll">
            <div className="platform-admin-brand-row">
              <div className="platform-admin-brand">
                {branding?.logoImageUrl
                  ? <img src={branding.logoImageUrl} alt="Servsolo" className="platform-admin-brand-logo" style={{ width: Math.min(Number(branding.logoWidthDesktop) || 160, 160) + 'px' }} />
                  : <>Serv<span className="platform-admin-brand-accent">Solo</span></>
                }
              </div>
              <div className="platform-admin-brand-controls">
                <button className="platform-admin-theme-toggle" onClick={toggleTheme} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
                  {darkMode ? '☀' : '☽'}
                </button>
              </div>
            </div>
            <div className="platform-admin-role-label">{isAdmin ? 'Admin' : 'User'}</div>

            <nav className="platform-admin-nav">
              {navItems.map((item) => (
                item.submenu ? (
                  <div key={item.key} className="platform-admin-nav-group">
                    <button
                      type="button"
                      className={`platform-admin-nav-link platform-admin-nav-link-toggle${
                        (page === 'site-content' && item.key === 'site-content') ||
                        (item.key === 'store-website' && storeSection === 'website') ||
                        (item.key === 'store' && (storeSection === 'admin' || storeSection === 'store')) ||
                        (item.key === 'products' && (storeSection === 'products' || storeSection === 'modifiers' || storeSection === 'style')) ||
                        (item.key === 'website' && storeSection === 'website') ||
                        (item.key === 'account' && page === 'account')
                          ? ' active' : ''
                      }`}
                      onClick={() => setExpandedNavKey((current) => (current === item.key ? null : item.key))}
                    >
                      <span>{item.label}</span>
                      <span className="platform-admin-nav-caret">{expandedNavKey === item.key ? '▾' : '▸'}</span>
                    </button>
                    {expandedNavKey === item.key && (
                      <div className="platform-admin-nav-submenu">
                        {item.submenu.map((sub) => (
                          sub.to ? (
                            <NavLink
                              key={sub.key}
                              to={sub.to}
                              className={({ isActive }) => `platform-admin-nav-sublink${isActive && new URLSearchParams(window.location.search).get('page') == null ? ' active' : ''}`}
                            >
                              {sub.label}
                            </NavLink>
                          ) : (
                            <button type="button" key={sub.key} className="platform-admin-nav-sublink" onClick={sub.action}>
                              {sub.label}
                            </button>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/dashboard'}
                    className={({ isActive }) => `platform-admin-nav-link${isActive ? ' active' : ''}`}
                  >
                    <span>{item.label}</span>
                    {item.count > 0 && <span className="platform-admin-nav-count">{item.count}</span>}
                  </NavLink>
                )
              ))}
            </nav>

            {headerActions.length > 0 && (
              <div className="platform-admin-sidebar-actions">
                {headerActions.map((action) => (
                  <NavLink
                    key={action.to}
                    to={action.to}
                    className={({ isActive }) => `platform-admin-sidebar-action${isActive ? ' active' : ''}`}
                  >
                    <span>{action.label}</span>
                    {action.count > 0 && <span className="platform-admin-nav-count">{action.count}</span>}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          <div className="platform-admin-sidebar-footer">
            <button type="button" className="platform-admin-command-trigger" onClick={() => setCommandOpen(true)}>
              <span>Jump to</span>
              <strong>Ctrl+K</strong>
            </button>
            <div className="platform-admin-sidebar-email">{user?.email || ''}</div>
            <a href="/" className="platform-admin-site-link">Back to Servsolo Home</a>
            <button type="button" className="platform-admin-signout-btn" onClick={handleSignOut}>Sign Out</button>
          </div>
        </aside>
      )}

      {/* Content */}
      <main className={`platform-admin-content${chromeHidden ? ' chrome-hidden' : ''}${page === 'store-editor' && chromeHidden ? ' store-focus' : ''}${page === 'messages' ? ' messages-page' : ''}${isFullscreenPreview ? ' platform-admin-content-fullscreen' : ''}`}>
        {!messagesReady && <div className="platform-admin-inline-note">Messages are not live in the database yet. Apply the latest Supabase migration to enable inbox features.</div>}
        {renderPage()}
      </main>

      {/* Command palette */}
      {commandOpen && (
        <div className="platform-admin-command-overlay" onClick={() => setCommandOpen(false)}>
          <div className="platform-admin-command-panel" onClick={(event) => event.stopPropagation()}>
            <div className="platform-admin-command-header">
              <div>
                <strong>Workspace switcher</strong>
                <span>Move between workspace sections without leaving the shell.</span>
              </div>
              <button type="button" className="platform-admin-header-btn secondary" onClick={() => setCommandOpen(false)}>Close</button>
            </div>
            <input
              autoFocus
              type="text"
              className="platform-admin-command-input"
              placeholder="Search sections, requests, messages..."
              value={commandQuery}
              onChange={(event) => setCommandQuery(event.target.value)}
            />
            <div className="platform-admin-command-list">
              {quickActions.length === 0 ? (
                <div className="platform-admin-empty">No matching workspace section.</div>
              ) : quickActions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="platform-admin-command-item"
                  onClick={() => handleQuickAction(action.to)}
                >
                  <div>
                    <strong>{action.label}</strong>
                    <span>{action.description}</span>
                  </div>
                  {action.count > 0 && <span className="platform-admin-nav-count">{action.count}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default PlatformAdminSite;